<template>
  <div class="wrapper">
    <RouterView />

    <BottomAppBar v-if="showLegacyDock" />

    <TasteClarificationModalWrapper />
    <SurveyModalWrapper />
    <GiftModalWrapper />
    <LookModalWrapper />

    <SearchImageModal />
    <AuthWrapper />

    <FairPageLoader />
    <FairVariantCompareTrayHost />
    <Toast />
  </div>
</template>

<script setup lang="ts">
import { RouterView, useRoute } from "vue-router";
import { computed, watch } from "vue";
import Toast from "primevue/toast";
import AuthWrapper from "@/features/auth/ui/modals/AuthWrapper.vue";
import SurveyModalWrapper from "@/features/survey/ui/modals/SurveyModalWrapper.vue";
import TasteClarificationModalWrapper from "@/features/taste/ui/modals/TasteClarificationModalWrapper.vue";
import GiftModalWrapper from "@/features/gift/ui/modals/GiftModalWrapper.vue";
import LookModalWrapper from "@/features/look/ui/modals/LookModalWrapper.vue";
import BottomAppBar from "@/features/bottom-app-bar/ui/BottomAppBar.vue";
import { useDevice } from "@/composables/useDevice";
import SearchImageModal from "@/features/search/components/image-flow/SearchImageModal.vue";
import { useTokenStore } from "@/features/auth/store";
import { useTasteClarificationModal } from "@/features/taste/store";
import { useSurveyModal } from "@/features/survey/store";
import { useLookModal } from "@/features/look/store";
import { useGiftModal } from "@/features/gift/store";
import { FairPageLoader } from "@/ui/patterns/FairPageLoader";
import FairVariantCompareTrayHost from "@/widgets/compare/FairVariantCompareTrayHost.vue";
import { HOME_ROUTE } from "@/router/routes";
import { USE_FAIR_HOME } from "@/widgets/home/fair/fair-home.flags";
import { USE_FAIR_SEARCH } from "@/widgets/search/fair/fair-search.flags";

const { isDevice } = useDevice();
const route = useRoute();
const deviceType = computed(() => isDevice.value);

const isFairHomeRoute = computed(() => {
  const path = route.path;
  return path === HOME_ROUTE || path === "/home";
});

const isFairSearchRoute = computed(() => {
  return route.path.startsWith("/searchList");
});

/** Fair Home / Search own floating dock below 1024 — skip legacy PrimeVue Dock. */
const showLegacyDock = computed(() => {
  const isCompactDevice =
    deviceType.value === "mobile" || deviceType.value === "tablet";
  if (!isCompactDevice) return false;
  if (USE_FAIR_HOME && isFairHomeRoute.value) return false;
  if (USE_FAIR_SEARCH && isFairSearchRoute.value) return false;
  return true;
});

// Security: close all modals when user is logged out (token cleared)
const tokenStore = useTokenStore();
watch(() => tokenStore.accessToken, (newToken, oldToken) => {
  if (oldToken && !newToken) {
    // Token was cleared — user logged out or session expired
    const tasteStore = useTasteClarificationModal();
    const surveyStore = useSurveyModal();
    const lookStore = useLookModal();
    const giftStore = useGiftModal();

    if (tasteStore.tasteModal.isShow) tasteStore.tasteModal.isShow = false;
    if (surveyStore.surveyModal?.isShow) surveyStore.surveyModal.isShow = false;
    if (lookStore.lookModal?.isShow) lookStore.lookModal.isShow = false;
    if (giftStore.giftModal?.isShow) giftStore.giftModal.isShow = false;
  }
});
</script>

<style scoped>
.wrapper {
  display: flex;
  justify-content: center;
}
</style>
